/** @jsxImportSource @emotion/react */
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormikErrors, useFormik } from 'formik';
import { useMerchantApplicationContext } from 'contexts';
import { Constants } from 'global';
import { ProgressBar } from 'components/sign-up';
import { StepComponentProps } from 'types/interfaces/sign-up/MerchantApplication.types';
import {
  ActivationStepKey,
  activationStepsConfig,
} from 'modules/activation/ActivationConfig';
import {
  getActivationRadioOptions,
  getInitialActivationStepData,
  getTransactionDetailsAIBannerState,
  getStoredAIPrefill,
  AI_CONFIDENCE_THRESHOLDS,
  meetsConfidence,
} from 'modules/activation/steps/activationStepData';
import { getStoredActivationData } from 'modules/activation/activationStorage';
import type { StoredActivationData } from 'modules/activation/activationStorage';
import type { MerchantApplicationResponse } from 'types/responses/activation';
import { ActivationRadioGroup } from '../ActivationRadioGroup';
import * as styles from 'modules/activation/ActivationFlow.styles';

const AIBanner = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}): JSX.Element => (
  <div css={styles.stepOneAIBanner} role="status">
    <img
      src={Constants.assets.signUpAssets.icons.aiSuggestionSmallSparkle}
      css={styles.stepOneAIIcon()}
      alt=""
      aria-hidden="true"
    />
    <div css={styles.stepOneBannerCopy}>
      <p className="body1" css={styles.stepOneBannerTitle}>
        {title}
      </p>
      {subtitle && (
        <p className="body4" css={styles.stepOneBannerSubtitle}>
          {subtitle}
        </p>
      )}
    </div>
  </div>
);

type ActivationTransactionDetailsProps = {
  averageTransactionValue?: string;
  costOfLargestProductOrService?: string;
  averageShippingTimeFrame?: string;
  hasShippingPolicy?: boolean;
  hasRefundPolicy?: boolean;
};

export const getTransactionDetailsAIVisibility = (
  activationData:
    | StoredActivationData
    | MerchantApplicationResponse
    | null = getStoredActivationData(),
  isInstoreOnly = false
): {
  hideShippingPolicy: boolean;
  hideShippingTimeFrame: boolean;
  hideRefundPolicy: boolean;
} => {
  if (
    (activationData as StoredActivationData)?.businessDetailsStepBAIConfirmed
  ) {
    return {
      hideShippingPolicy: false,
      hideShippingTimeFrame: isInstoreOnly,
      hideRefundPolicy: false,
    };
  }

  const ai = getStoredAIPrefill(activationData);

  return {
    hideShippingPolicy:
      meetsConfidence(
        ai?.shippingPolicyConfidence,
        AI_CONFIDENCE_THRESHOLDS.shippingPolicy
      ) && ai?.shippingPolicy != null,
    hideShippingTimeFrame:
      isInstoreOnly ||
      (meetsConfidence(
        ai?.avgShipTimeConfidence,
        AI_CONFIDENCE_THRESHOLDS.avgShipTime
      ) &&
        ai?.avgShipTime === '0–7 days'),
    hideRefundPolicy:
      meetsConfidence(
        ai?.returnPolicyConfidence,
        AI_CONFIDENCE_THRESHOLDS.returnPolicy
      ) && ai?.returnPolicy != null,
  };
};

const emptyValues: ActivationTransactionDetailsProps = {
  averageTransactionValue: '',
  costOfLargestProductOrService: '',
  averageShippingTimeFrame: '',
  hasShippingPolicy: undefined,
  hasRefundPolicy: undefined,
};

const validateValues = (
  values: ActivationTransactionDetailsProps,
  visibility: ReturnType<typeof getTransactionDetailsAIVisibility>
): FormikErrors<ActivationTransactionDetailsProps> => {
  const errors: FormikErrors<ActivationTransactionDetailsProps> = {};

  if (!values.averageTransactionValue) {
    errors.averageTransactionValue = 'Please select an average transaction';
  }

  if (!values.costOfLargestProductOrService) {
    errors.costOfLargestProductOrService =
      'Please select the largest product or service cost';
  }

  if (
    !visibility.hideShippingPolicy &&
    values.hasShippingPolicy === undefined
  ) {
    errors.hasShippingPolicy = 'Please select an option';
  }

  if (!visibility.hideShippingTimeFrame && !values.averageShippingTimeFrame) {
    errors.averageShippingTimeFrame = 'Please select a shipping timeframe';
  }

  if (!visibility.hideRefundPolicy && values.hasRefundPolicy === undefined) {
    errors.hasRefundPolicy = 'Please select an option';
  }

  return errors;
};

export const TransactionDetailsStepModule: FC<StepComponentProps> = ({
  provideSubmit,
  canContinue = true,
  isSubmitting = false,
  onContinue,
  canGoBack,
  onBack,
}) => {
  const { activationData, getActivationStepData, setActivationStepData } =
    useMerchantApplicationContext();
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const savedValuesRef = useRef(
    getActivationStepData(ActivationStepKey.TransactionDetails)
  );
  // Fall back to a localStorage snapshot taken at mount so the form is
  // populated immediately on first render, before the API fetch resolves.
  const cachedActivationData = useMemo(() => getStoredActivationData(), []);
  const activationDataSource = activationData ?? cachedActivationData;

  const bannerState = getTransactionDetailsAIBannerState(activationDataSource);
  const isInstoreOnly =
    activationDataSource?.signUpInfo?.paymentType === 'Instore';
  const visibility = getTransactionDetailsAIVisibility(
    activationDataSource,
    isInstoreOnly
  );

  const initialValues = useMemo(
    () => ({
      ...emptyValues,
      ...getInitialActivationStepData(
        ActivationStepKey.TransactionDetails,
        activationDataSource
      ),
      ...(savedValuesRef.current ?? {}),
    }),
    [activationDataSource]
  );

  const averageTransactionOptions = getActivationRadioOptions(
    'averageTransactionValue'
  );
  const largestProductOptions = getActivationRadioOptions(
    'costOfLargestProductOrService'
  );
  const shippingTimeFrameOptions = getActivationRadioOptions(
    'averageShippingTimeFrame'
  );

  const formik = useFormik<ActivationTransactionDetailsProps>({
    initialValues,
    enableReinitialize: true,
    validate: (values) => validateValues(values, visibility),
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      setActivationStepData(ActivationStepKey.TransactionDetails, values, true);
    },
  });

  useEffect(() => {
    if (submitAttempted) {
      formik.validateForm();
    }

    setActivationStepData(
      ActivationStepKey.TransactionDetails,
      formik.values,
      true
    );
  }, [formik.values, submitAttempted, setActivationStepData]);

  useEffect(() => {
    provideSubmit?.(async () => {
      setSubmitAttempted(true);

      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          {
            averageTransactionValue: true,
            costOfLargestProductOrService: true,
            averageShippingTimeFrame: true,
            hasShippingPolicy: true,
            hasRefundPolicy: true,
          },
          false
        );
        return false;
      }

      await formik.submitForm();
      return true;
    });
  }, [provideSubmit, formik]);

  return (
    <div css={styles.stepOneRoot}>
      {bannerState !== 'none' && (
        <AIBanner
          title={
            bannerState === 'full'
              ? "We've pre-filled your details"
              : "We've pre-filled some details"
          }
          subtitle="Please check that your details are correct"
        />
      )}
      <div css={styles.stepOnePageWrapper}>
        <div css={styles.stepOnePanel}>
          <div css={styles.stepOneHeader}>
            <h1 className="heading1" css={styles.stepOneTitle}>
              Business details
            </h1>
            <ProgressBar
              steps={activationStepsConfig}
              activeStepKey={ActivationStepKey.TransactionDetails}
            />
          </div>
          <form css={styles.stepOneForm}>
            <ActivationRadioGroup
              name="averageTransactionValue"
              label="Average transaction value"
              options={averageTransactionOptions}
              value={formik.values.averageTransactionValue}
              onChange={(value) =>
                formik.setFieldValue('averageTransactionValue', value)
              }
              error={
                submitAttempted &&
                Boolean(formik.errors.averageTransactionValue)
              }
              helperText={
                submitAttempted
                  ? formik.errors.averageTransactionValue
                  : undefined
              }
            />

            <ActivationRadioGroup
              name="costOfLargestProductOrService"
              label="Cost of your largest product or service"
              options={largestProductOptions}
              value={formik.values.costOfLargestProductOrService}
              onChange={(value) =>
                formik.setFieldValue('costOfLargestProductOrService', value)
              }
              error={
                submitAttempted &&
                Boolean(formik.errors.costOfLargestProductOrService)
              }
              helperText={
                submitAttempted
                  ? formik.errors.costOfLargestProductOrService
                  : undefined
              }
            />

            {!visibility.hideShippingPolicy && (
              <ActivationRadioGroup
                name="hasShippingPolicy"
                label="Do you have a shipping policy?"
                value={formik.values.hasShippingPolicy}
                onChange={(value) =>
                  formik.setFieldValue('hasShippingPolicy', value)
                }
                error={
                  submitAttempted && Boolean(formik.errors.hasShippingPolicy)
                }
                helperText={
                  submitAttempted ? formik.errors.hasShippingPolicy : undefined
                }
              />
            )}

            {!visibility.hideShippingTimeFrame && (
              <ActivationRadioGroup
                name="averageShippingTimeFrame"
                label="Average shipping timeframe"
                options={shippingTimeFrameOptions}
                value={formik.values.averageShippingTimeFrame}
                onChange={(value) =>
                  formik.setFieldValue('averageShippingTimeFrame', value)
                }
                error={
                  submitAttempted &&
                  Boolean(formik.errors.averageShippingTimeFrame)
                }
                helperText={
                  submitAttempted
                    ? formik.errors.averageShippingTimeFrame
                    : undefined
                }
              />
            )}

            {!visibility.hideRefundPolicy && (
              <ActivationRadioGroup
                name="hasRefundPolicy"
                label="Do you have a refund policy?"
                value={formik.values.hasRefundPolicy}
                onChange={(value) =>
                  formik.setFieldValue('hasRefundPolicy', value)
                }
                error={
                  submitAttempted && Boolean(formik.errors.hasRefundPolicy)
                }
                helperText={
                  submitAttempted ? formik.errors.hasRefundPolicy : undefined
                }
              />
            )}
            <div css={[styles.stepOneActionRow, { marginTop: 0 }]}>
              {canGoBack && (
                <button
                  className="body1"
                  type="button"
                  css={styles.stepOneReturnButton}
                  onClick={onBack}
                >
                  Back
                </button>
              )}
              <button
                className="body1"
                type="button"
                css={styles.stepOneNextButton}
                disabled={!canContinue || isSubmitting}
                onClick={onContinue}
              >
                Next
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsStepModule;
